import type { ReactNode } from 'react';
import './XiangqiRulePaper.css';

export type EasternPoint = readonly [number, number];
export type EasternPiece = { at: EasternPoint; glyph: string; first?: boolean };
type PictureProps = {
  name: string; game?: 'xiangqi'|'janggi'; pieces: EasternPiece[]; label: string;
  targets?: EasternPoint[]; blocked?: EasternPoint[]; paths?: EasternPoint[][];
  crop?: { start: EasternPoint; files: number; ranks: number }; unit?: number;
};
/** All comparison pictures keep their grid dimensions and unit equal. Rank zero is at the bottom. */
export function EasternBoardPicture({name,game='xiangqi',pieces,label,targets=[],blocked=[],paths=[],crop,unit=24}:PictureProps) {
  const start=crop?.start??[0,0], files=crop?.files??9, ranks=crop?.ranks??10, margin=12;
  const w=(files-1)*unit+margin*2,h=(ranks-1)*unit+margin*2;
  const xy=([f,r]:EasternPoint)=>[margin+(f-start[0])*unit,margin+(start[1]+ranks-1-r)*unit];
  const visible=([f,r]:EasternPoint)=>f>=start[0]&&f<start[0]+files&&r>=start[1]&&r<start[1]+ranks;
  const riverY=xy([0,4.5])[1];
  return <svg viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label} data-eastern-board={name}
    data-files={files} data-ranks={ranks} data-grid-unit={unit} data-origin={start.join(',')}>
    <rect width={w} height={h} rx="3" fill="#fffefa"/>
    {game==='xiangqi'&&start[1]<=4&&start[1]+ranks>5&&<rect x={margin} y={riverY-unit/2} width={(files-1)*unit} height={unit} fill="#e4eee8"/>}
    {Array.from({length:ranks},(_,n)=>{const y=margin+n*unit;return <line key={`r${n}`} x1={margin} x2={w-margin} y1={y} y2={y} stroke="#c5c8b9" strokeWidth=".75"/>;})}
    {Array.from({length:files},(_,n)=>{const x=margin+n*unit,f=n+start[0];return game==='xiangqi'&&f>0&&f<8&&start[1]<=4&&start[1]+ranks>5?
      <path key={`f${n}`} d={`M${x} ${margin}V${riverY-unit/2}M${x} ${riverY+unit/2}V${h-margin}`} fill="none" stroke="#c5c8b9" strokeWidth=".75"/>:
      <line key={`f${n}`} x1={x} x2={x} y1={margin} y2={h-margin} stroke="#c5c8b9" strokeWidth=".75"/>;})}
    {[0,7].flatMap(r=>[[[3,r],[5,r+2]],[[5,r],[3,r+2]]] as EasternPoint[][]).map((points,n)=> {
      if(!points.every(visible))return null;const a=xy(points[0]),b=xy(points[1]);return <line key={`palace${n}`} x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke="#939b86" strokeWidth="1"/>;
    })}
    {targets.filter(visible).map((at,n)=>{const[x,y]=xy(at);return <circle key={`target${n}`} cx={x} cy={y} r={unit*.30} fill="#e3e9d5" stroke="#6f7c57" strokeWidth="1"/>;})}
    {paths.map((points,n)=>{const coords=points.map(xy),[ex,ey]=coords.at(-1)!,[px,py]=coords.at(-2)!;
      const len=Math.hypot(ex-px,ey-py),dx=(ex-px)/len,dy=(ey-py)/len;
      const end=points.at(-1)!,occupied=pieces.some(p=>p.at[0]===end[0]&&p.at[1]===end[1]);
      const x=ex-(occupied?unit*.43*dx:0),y=ey-(occupied?unit*.43*dy:0);
      return <g key={`path${n}`} data-arrow-purpose="move"><polyline points={coords.map(p=>p.join(',')).join(' ')} fill="none" stroke="#75825e" strokeWidth="1.5"/>
        <path d={`M${x-5*dx+3*dy} ${y-5*dy-3*dx}L${x} ${y}L${x-5*dx-3*dy} ${y-5*dy+3*dx}`} fill="none" stroke="#75825e" strokeWidth="1.5"/></g>;
    })}
    {pieces.filter(p=>visible(p.at)).map(({at,glyph,first},n)=>{const[x,y]=xy(at),color=game==='janggi'?(first?'#1f7a4d':'#c0392b'):(first?'#a74b3d':'#343a33');
      return <g key={`piece${n}`} data-piece={glyph} data-point={at.join(',')} data-owner={first?'First':'Second'}><circle cx={x} cy={y} r={unit*.36} fill="#fffefa" stroke={color} strokeWidth="1.2"/>
        <text x={x} y={y+unit*.22} textAnchor="middle" fontSize={unit*.64} fill={color} fontWeight="600">{glyph}</text></g>;
    })}
    {blocked.filter(visible).map((at,n)=>{const[x,y]=xy(at);return <path key={`blocked${n}`} d={`M${x-4} ${y-4}l8 8m0 -8l-8 8`} fill="none" stroke="#a66b4e" strokeWidth="1.8"/>;})}
  </svg>;
}
export function EasternPanel({children,caption}:{children:ReactNode;caption:string}) {return <figure>{children}<figcaption>{caption}</figcaption></figure>;}
export function EasternPair({name,children,game='xiangqi'}:{name:string;children:ReactNode;game?:'xiangqi'|'janggi'}) {
  return <div className={`${game}-pair`} data-eastern-comparison={name}>{children}</div>;
}
const at=(f:number,r:number,glyph:string,first=true):EasternPiece=>({at:[f,r],glyph,first});
const mini={start:[2,2] as EasternPoint,files:5,ranks:5};
const horseEnds:EasternPoint[]=[[2,3],[2,5],[3,2],[3,6],[5,2],[5,6],[6,3],[6,5]];
function Mate() {
  const pieces=[at(0,9,'俥'),at(3,8,'俥'),at(4,9,'將',false),at(3,7,'士',false),at(5,7,'士',false),at(3,0,'帥'),
    ...[0,2,6,8].map(f=>at(f,6,'卒',false)),...[0,2,4,6,8].map(f=>at(f,3,'兵'))];
  return <figure className="xiangqi-mate"><EasternBoardPicture name="xiangqi-mate" pieces={pieces} targets={[[3,9],[4,8],[5,9]]}
    blocked={[[3,9],[4,8],[5,9]]} unit={22} label="9×10の全盤。川と両九宮があります。赤の俥が黒の將へ王手をかけ、黒の王の移動先も赤の俥に攻撃されています。黒番ですが、どの駒を動かしても王手を解消できず詰みです。"/>
    <figcaption>黒の王は王手を解消できません。</figcaption></figure>;
}
function PalaceMoves() {
  const crop={start:[3,0] as EasternPoint,files:3,ranks:3};
  return <EasternPair name="xiangqi-palace">
    <EasternPanel caption="王は縦横へ1マス動けます。"><EasternBoardPicture name="xiangqi-general" crop={crop} pieces={[at(4,1,'帥')]} targets={[[3,1],[5,1],[4,0],[4,2]]}
      paths={[[[4,1],[4,2]]]} label="自分の九宮3×3。中央の帥は縦横に1マス動けます。"/></EasternPanel>
    <EasternPanel caption="仕・士は斜めへ1マス動けます。"><EasternBoardPicture name="xiangqi-advisor" crop={crop} pieces={[at(4,1,'仕')]} targets={[[3,0],[5,0],[3,2],[5,2]]}
      paths={[[[4,1],[3,2]]]} label="同じ3×3の九宮、同じ24単位の間隔。中央の仕は斜めに1マス動けます。"/></EasternPanel>
  </EasternPair>;
}
function HorseMoves() {
  return <EasternPair name="xiangqi-horse">
    <EasternPanel caption="縦横1マスと斜め1マスを1手で進みます。"><EasternBoardPicture name="xiangqi-horse-clear" crop={mini} pieces={[at(4,4,'馬')]} targets={horseEnds}
      paths={[[[4,4],[4,5],[3,6]],[[4,4],[5,4],[6,5]]]} label="5×5の範囲。馬は縦横1マス、続いて外向きの斜め1マスへ、止まらず1手で動きます。空いた途中の交点を通る8方向があります。"/></EasternPanel>
    <EasternPanel caption="途中の駒で、2方向が塞がれます。"><EasternBoardPicture name="xiangqi-horse-blocked" crop={mini} pieces={[at(4,4,'馬'),at(4,5,'兵')]}
      targets={horseEnds.filter(p=>p[1]!==6)} blocked={[[3,6],[5,6]]} label="左と同じ5×5、24単位の間隔。馬のすぐ前に兵があるので、そこを通る左前・右前の2方向には動けません。"/></EasternPanel>
  </EasternPair>;
}
function ElephantMoves() {
  const crop={start:[2,0] as EasternPoint,files:5,ranks:5},ends:EasternPoint[]=[[2,0],[6,0],[2,4],[6,4]];
  return <>
    <EasternPair name="xiangqi-elephant-eye">
      <EasternPanel caption="斜めに2マス進みます。"><EasternBoardPicture name="xiangqi-elephant-clear" crop={crop} pieces={[at(4,2,'相')]} targets={ends}
        paths={[[[4,2],[5,3],[6,4]]]} label="5×5の同じ範囲。相は斜め2マスへ1手で動き、途中の交点は空いています。"/></EasternPanel>
      <EasternPanel caption="途中に駒がある方向へは動けません。"><EasternBoardPicture name="xiangqi-elephant-blocked" crop={crop} pieces={[at(4,2,'相'),at(5,3,'兵')]}
        targets={ends.filter(p=>p[0]!==6||p[1]!==4)} blocked={[[6,4]]} label="左と同じ5×5、24単位の間隔。右前への経路の中間に兵があるので、その方向には動けません。"/></EasternPanel>
    </EasternPair>
    <figure className="xiangqi-full"><EasternBoardPicture name="xiangqi-elephant-river" pieces={[at(4,4,'相')]} targets={[[2,2],[6,2]]} blocked={[[2,6],[6,6]]}
      paths={[[[4,4],[3,3],[2,2]]]} unit={22} label="9×10の全盤。相は川を越せないので、自陣内の斜め2マスへは動けますが、川の向こうの2方向へは動けません。"/>
      <figcaption>相・象は川を越えられません。</figcaption></figure>
  </>;
}
function ChariotMoves() {
  const crop={start:[1,1] as EasternPoint,files:7,ranks:7};
  return <figure className="xiangqi-atlas-wide"><EasternBoardPicture name="xiangqi-chariot" crop={crop} unit={18} pieces={[at(4,4,'俥'),at(4,6,'兵')]}
    paths={[[[4,4],[1,4]],[[4,4],[7,4]],[[4,4],[4,1]],[[4,4],[4,5]]]} targets={[[1,4],[7,4],[4,1],[4,5]]} blocked={[[4,7]]}
    label="7×7の範囲。俥は縦横の空いた交点を何マスでも進めます。上の兵を飛び越えて、その先へは動けません。"/>
    <figcaption>途中の駒は飛び越せません。</figcaption></figure>;
}
function CannonMoves() {
  const crop={start:[0,3] as EasternPoint,files:7,ranks:3};
  return <>
    <figure className="xiangqi-cannon-row"><EasternBoardPicture name="xiangqi-cannon-step" crop={crop} unit={22} pieces={[at(0,4,'炮')]}
      targets={[[5,4]]} paths={[[[0,4],[5,4]]]} label="7×3の範囲。炮が駒を取らずに、途中の駒のない横の線を進みます。"/>
      <figcaption>駒を取らないときは間の駒を飛び越えずに進みます。</figcaption></figure>
    <div className="xiangqi-cannon-stack" data-eastern-comparison="xiangqi-cannon-screens">
      {[0,1,2].map(count=><EasternPanel key={count} caption={count===1?'間の駒が1枚なら、相手の駒を取れます。':`間の駒が${count}枚なので、相手の駒は取れません。`}>
        <EasternBoardPicture name={`xiangqi-cannon-${count}`} crop={crop} unit={22}
          pieces={[at(0,4,'炮'),at(6,4,'卒',false),...(count>=1?[at(3,4,'兵')]:[]),...(count===2?[at(4,4,'卒',false)]:[])]}
          paths={count===1?[[[0,4],[6,4]]]:[]} blocked={count===1?[]:[[6,4]]}
          label={`同じ7×3の範囲、22単位の間隔。炮と相手の卒の間に${count}枚の駒があります。${count===1?'1枚だけを飛び越えて卒を取れます。':'間がちょうど1枚ではないので、卒を取れません。'}`}/>
      </EasternPanel>)}
    </div>
  </>;
}
function PawnMoves() {
  const crop={start:[2,3] as EasternPoint,files:5,ranks:4};
  return <EasternPair name="xiangqi-pawn-river">
    <EasternPanel caption="川を越える前は前へ1マス動けます。"><EasternBoardPicture name="xiangqi-pawn-before" crop={crop} pieces={[at(4,3,'兵')]} targets={[[4,4]]}
      paths={[[[4,3],[4,4]]]} label="川の両側を含む5×4の範囲。川を越える前の兵は、前へ1マスだけ動けます。"/></EasternPanel>
    <EasternPanel caption="川を越えたら、前か横へ1マス動けます。"><EasternBoardPicture name="xiangqi-pawn-after" crop={crop} pieces={[at(4,5,'兵')]} targets={[[4,6],[3,5],[5,5]]}
      paths={[[[4,5],[3,5]]]} blocked={[[4,4]]} label="左と同じ5×4、24単位の間隔。川を越えた兵は前と左右へ1マス動けますが、後ろへは戻れません。"/></EasternPanel>
  </EasternPair>;
}
function FacingKings() {
  return <div className="xiangqi-full-stack" data-eastern-comparison="xiangqi-facing-kings">
    <EasternPanel caption="王どうしの間に、俥があります。"><EasternBoardPicture name="xiangqi-facing-before" unit={22} pieces={[at(4,0,'帥'),at(4,9,'將',false),at(4,4,'俥')]}
      label="9×10の全盤。両王は同じ縦の線ですが、間に赤の俥があるので、王どうしの向かい合いは禁止の状態ではありません。"/></EasternPanel>
    <EasternPanel caption="間が空くこの手は指せません。"><EasternBoardPicture name="xiangqi-facing-after" unit={22} pieces={[at(4,0,'帥'),at(4,9,'將',false),at(3,4,'俥')]}
      blocked={[[3,4]]} label="左と同じ9×10、22単位の間隔。赤の俥を横へ動かすと両王の間が空になるので、この手は指せません。前後の状態を移動矢印では結んでいません。"/></EasternPanel>
  </div>;
}
export function XiangqiRulePaper() {
  return <article className="rule-paper xiangqi-paper">
    <div data-xiangqi-primary className="xiangqi-primary">
      <section><h2>相手の王を詰ませると勝ちです。</h2>
        <p>王への攻撃を王手といいます。相手が王手を解消できない状態が詰みです。</p>
        <Mate/>
      </section>
      <section className="xiangqi-start"><p>赤から交互に自分の駒を1枚ずつ動かします。駒は盤の交点に置きます。</p>
        <p>自分の王が王手になる手は指せません。</p>
      </section>
    </div>
    <div className="xiangqi-details">
      <section data-xiangqi-detail="palace"><h3>王と仕・士は自陣の3×3（九宮）から出られません。</h3><PalaceMoves/>
        <p>取った駒はもう使えません。駒は成りません。</p></section>
      <section data-xiangqi-detail="chariot"><h3>俥・車は縦横に何マスでも動けます。</h3><ChariotMoves/></section>
      <section data-xiangqi-detail="horse"><h3>馬は途中を通って1手で進みます。</h3>
        <p>縦横1マスと斜め1マスをつないだ道を進みます。途中の交点に駒がある方向へは動けません。</p><HorseMoves/></section>
      <section data-xiangqi-detail="elephant"><h3>相・象は斜めに2マス動けます。</h3><ElephantMoves/></section>
      <section data-xiangqi-detail="cannon"><h3>炮・砲は取るときだけ1枚飛び越えます。</h3>
        <p>縦横に何マスでも動きます。取るときに飛び越える1枚は自分の駒でも相手の駒でもかまいません。</p><CannonMoves/></section>
      <section data-xiangqi-detail="pawn"><h3>兵・卒は川を越えると横にも動けます。</h3><PawnMoves/><p>後ろには動けません。</p></section>
      <section data-xiangqi-detail="facing"><h3>王どうしの間を空けてはいけません。</h3>
        <p>王どうしが同じ縦の線に並び、間に駒がなくなる手は指せません。</p><FacingKings/></section>
      <section data-xiangqi-detail="ending"><h3>手がなくなると負けです。</h3><p>王手でなくても、指せる手がなくなった側は負けです。</p>
        <h3>繰り返しと引き分け</h3><p>駒の配置と手番が同じ局面が3回現れたとき、片方だけが王手をかけ続けていれば、その側の負けです。</p>
        <p>駒を取らず、兵・卒も動かさないまま双方が60手ずつ指すと、引き分けです。</p></section>
    </div>
  </article>;
}
